import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const ModuleView = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [quizStatus, setQuizStatus] = useState(null);
  const [moduleHasQuiz, setModuleHasQuiz] = useState(false);

  useEffect(() => {
    fetchTasks();
    fetchQuizStatus();
  }, [moduleId]);

  const fetchTasks = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/instructional/modules/${moduleId}/tasks`
      );
      setTasks(res.data.tasks);
    } catch (err) {
      console.error('Fetch tasks error:', err);
    } finally {
      setLoading(false);
    }
  };
  const fetchQuizStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/quiz/modules/${moduleId}/take`);
      setModuleHasQuiz(res.data.questions.length > 0);
      setQuizStatus(res.data.bestAttempt);
    } catch (err) {
      console.error('Quiz status error:', err);
    }
  };

  const handleCompleteTask = async (taskId) => {
    setCompleting(taskId);
    try {
      await axios.post(
        `${API_URL}/api/instructional/tasks/${taskId}/complete`
      );
      fetchTasks();
    } catch (err) {
      console.error('Complete task error:', err);
    } finally {
      setCompleting(null);
    }
  };

  const completedCount = tasks.filter(t => t.completed).length;
  const totalCount = tasks.length;
  const progressPercent = totalCount > 0 ? 
    Math.round((completedCount / totalCount) * 100) : 0;

  const taskTypeLabel = (type) => {
    const types = {
      lecture: '🎥 Lecture',
      workbook: '📓 Workbook',
      discussion: '💬 Discussion Board',
      live_discussion: '🎙️ Live Discussion',
      live_practice: '🤝 Live Practice',
      quiz: '📝 Quiz'
    };
    return types[type] || type;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {user?.first_name}</span>
          <button
            style={styles.returnBtn}
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>
      </div>

      <div style={styles.main}>
        {loading && (
          <div style={styles.centered}>
            <p style={styles.loadingText}>Loading tasks...</p>
          </div>
        )}

        {!loading && (
          <>
            {/* Progress Bar */}
            <div style={styles.progressCard}>
              <div style={styles.progressHeader}>
                <span style={styles.progressTitle}>Module Progress</span>
                <span style={styles.progressCount}>
                  {completedCount} of {totalCount} tasks complete
                </span>
              </div>
              <div style={styles.progressBarBg}>
                <div 
                  style={{
                    ...styles.progressBarFill,
                    width: `${progressPercent}%`
                  }} 
                />
              </div>
              <span style={styles.progressPercent}>
                {progressPercent}%
              </span>
            </div>

            {/* Tasks List */}
            <div style={styles.tasksContainer}>
              {tasks.map((task, index) => (
                <div 
                  key={task.id} 
                  style={task.completed ? 
                    styles.taskCardComplete : styles.taskCard}
                >
                  <div style={styles.taskLeft}>
                    <div style={task.completed ? 
                      styles.checkComplete : styles.checkEmpty}>
                      {task.completed && '✓'}
                    </div>
                    <div>
                      <div style={styles.taskType}>
                        {taskTypeLabel(task.task_type)}
                      </div>
                      <h4 style={styles.taskTitle}>{task.title}</h4>
                    </div>
                  </div>
                  <div style={styles.taskRight}>
                    {!task.completed && (
                      <button
                        style={styles.enterTaskBtn}
                        onClick={() => navigate(`/instructional/task/${task.id}`)}
                      >
                        Start →
                      </button>
                    )}
                    {task.completed && (
                      <button
                        style={styles.reviewBtn}
                        onClick={() => navigate(`/instructional/task/${task.id}`)}
                      >
                        Review
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Quiz Section */}
            {moduleHasQuiz && (
              <div style={styles.quizSection}>
                <div style={styles.quizCard}>
                  <div style={styles.quizCardLeft}>
                    <h4 style={styles.quizCardTitle}>📝 Module Quiz</h4>
                    <p style={styles.quizCardSubtitle}>
                      {quizStatus?.passed
                        ? `✅ Passed with ${quizStatus.score}%`
                        : quizStatus
                        ? `Last attempt: ${quizStatus.score}% — Retake to pass`
                        : 'Complete the quiz to unlock the next module'}
                    </p>
                  </div>
                  <button
                    style={quizStatus?.passed ? 
                      styles.quizPassedBtn : styles.quizTakeBtn}
                    onClick={() => navigate(`/quiz/${moduleId}`)}
                  >
                    {quizStatus?.passed ? 'Review Quiz' : 
                     quizStatus ? 'Retake Quiz' : 'Take Quiz →'}
                  </button>
                </div>
              </div>
            )}

            {/* Complete Module */}
            {progressPercent === 100 && !moduleHasQuiz && (
              <div style={styles.completeSection}>
                <div style={styles.completeCard}>
                  <p style={styles.completeText}>
                    🎉 All tasks complete! Great work this week.
                  </p>
                </div>
              </div>
            )}

            {progressPercent === 100 && moduleHasQuiz && quizStatus?.passed && (
              <div style={styles.completeSection}>
                <div style={styles.completeCard}>
                  <p style={styles.completeText}>
                    🎉 All tasks complete and quiz passed! Module unlocked.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A2744',
    color: '#ffffff',
  },
  header: {
    backgroundColor: '#0f1a33',
    borderBottom: '1px solid #C49A28',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    color: '#C49A28',
    fontSize: '24px',
    fontWeight: '800',
    margin: '0',
    letterSpacing: '2px',
  },
  logoText: {
    color: '#ffffff80',
    fontSize: '14px',
    letterSpacing: '1px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  welcome: {
    color: '#ffffff',
    fontSize: '14px',
  },
  returnBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #C49A28',
    color: '#C49A28',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  main: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '32px',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: '60px',
  },
  loadingText: {
    color: '#ffffff60',
    fontSize: '16px',
  },
  progressCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '10px',
    padding: '20px 24px',
    marginBottom: '24px',
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  progressTitle: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
  },
  progressCount: {
    color: '#ffffff60',
    fontSize: '13px',
  },
  progressBarBg: {
    backgroundColor: '#1A2744',
    borderRadius: '999px',
    height: '8px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressBarFill: {
    backgroundColor: '#C49A28',
    height: '100%',
    borderRadius: '999px',
    transition: 'width 0.3s ease',
  },
  progressPercent: {
    color: '#C49A28',
    fontSize: '13px',
    fontWeight: '700',
  },
  tasksContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  taskCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '10px',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  taskCardComplete: {
    backgroundColor: '#0f1a33',
    border: '1px solid #1E844940',
    borderRadius: '10px',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    opacity: 0.8,
  },
  taskLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  checkEmpty: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid #ffffff30',
    flexShrink: 0,
  },
  checkComplete: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#1E8449',
    border: '2px solid #1E8449',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
    flexShrink: 0,
  },
  taskType: {
    color: '#1A6B8A',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  taskTitle: {
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
    margin: '0',
  },
  taskRight: {
    display: 'flex',
    gap: '8px',
  },
  enterTaskBtn: {
    backgroundColor: '#C49A28',
    color: '#1A2744',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 20px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  reviewBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #1E8449',
    color: '#1E8449',
    borderRadius: '6px',
    padding: '8px 20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  completeSection: {
    marginTop: '24px',
  },
  completeCard: {
    backgroundColor: '#1E844920',
    border: '1px solid #1E8449',
    borderRadius: '10px',
    padding: '20px 24px',
    textAlign: 'center',
  },
  completeText: {
    color: '#1E8449',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0',
  },
  quizSection: { marginTop: '24px' },
  quizCard: { backgroundColor: '#0f1a33', border: '1px solid #C49A2840', borderRadius: '10px', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  quizCardLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  quizCardTitle: { color: '#C49A28', fontSize: '16px', fontWeight: '700', margin: '0' },
  quizCardSubtitle: { color: '#ffffff60', fontSize: '13px', margin: '0' },
  quizTakeBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '10px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  quizPassedBtn: { backgroundColor: 'transparent', border: '1px solid #1E8449', color: '#1E8449', borderRadius: '6px', padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default ModuleView;